import React from 'react';
import PropTypes from 'prop-types';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { IMaskInput } from 'react-imask';

import {
  Box,
  Button,
  Container,
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  Checkbox,
  InputLabel,
  OutlinedInput,
  Snackbar,
  Alert,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  CircularProgress,
} from '@mui/material';

import { blue, lightBlue, blueGrey, teal, green } from '@mui/material/colors';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';

// 서비스 메타 정보
const SERVICE_META = {
  daclair:        { label: '다끌레어 서비스',    color: blue[600] },
  env_report:     { label: 'ENV-REPORT',         color: teal[600] },
  env_ad:         { label: 'ENV-AD',             color: green[700] },
  vcm:            { label: '탄소중립마켓 (VCM)', color: '#00838f' },
  cleaning_cart:  { label: '청소 카트',           color: blueGrey[600] },
  tumbler_washer: { label: '개인컵세척기',        color: lightBlue[600] },
  general:        { label: '일반 문의',           color: blueGrey[500] },
};

// 구입 주문서를 받는 제품 서비스
const PRODUCT_SERVICES = new Set(['cleaning_cart', 'tumbler_washer']);

// 전화번호 마스크
const PhoneMask = React.forwardRef(function PhoneMask(props, ref) {
  const { onChange, ...other } = props;
  return (
    <IMaskInput
      {...other}
      mask="000-0000-0000"
      inputRef={ref}
      onAccept={(value) => onChange({ target: { name: props.name, value } })}
    />
  );
});
PhoneMask.propTypes = {
  name: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
};

const SUBMIT_COOLDOWN_MS = 60 * 1000;
function isOnCooldown() {
  const last = sessionStorage.getItem('contact_last_submit');
  if (!last) return false;
  return Date.now() - parseInt(last, 10) < SUBMIT_COOLDOWN_MS;
}
function markSubmitted() {
  sessionStorage.setItem('contact_last_submit', String(Date.now()));
}

export default function ContactUsPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const initialKey = SERVICE_META[searchParams.get('service')]
    ? searchParams.get('service')
    : 'general';

  const initialType = (
    PRODUCT_SERVICES.has(initialKey) && searchParams.get('type') === 'purchase'
  ) ? 'purchase' : 'general';

  const [selectedService, setSelectedService] = React.useState(initialKey);
  const [inquiryType, setInquiryType] = React.useState(initialType); // 'purchase' | 'general'

  const serviceKey = selectedService;
  const serviceMeta = SERVICE_META[serviceKey];
  const isProductService = PRODUCT_SERVICES.has(serviceKey);
  const isPurchase = isProductService && inquiryType === 'purchase';

  const [form, setForm] = React.useState({
    name: '',
    position: '',
    email: '',
    phone: '',
    company: '',
    country: '',
    message: '',
    agreePrivacy: false,
    website: '', // 허니팟
  });

  // 구입 주문서 전용 필드
  const [purchase, setPurchase] = React.useState({
    quantity: '',
    deliveryAddress: '',
    deliveryDate: '',
    notes: '',
  });

  const [loading, setLoading] = React.useState(false);
  const [submitted, setSubmitted] = React.useState(false);
  const [snack, setSnack] = React.useState({ open: false, severity: 'error', msg: '' });

  const handleServiceChange = (key) => {
    setSelectedService(key);
    if (!PRODUCT_SERVICES.has(key)) {
      setInquiryType('general');
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handlePurchaseChange = (e) => {
    const { name, value } = e.target;
    setPurchase((prev) => ({ ...prev, [name]: value }));
  };

  // 구입 주문서를 message 필드 포맷으로 조립
  const buildPurchaseMessage = () => {
    const productName = SERVICE_META[serviceKey].label;
    const lines = [
      `[구입 주문서]`,
      `제품: ${productName}`,
      `구매 수량: ${purchase.quantity}대`,
      `납품처 주소: ${purchase.deliveryAddress || '-'}`,
      `납품 희망 일정: ${purchase.deliveryDate || '-'}`,
    ];
    if (purchase.notes.trim()) {
      lines.push(``, `추가 요청사항:`, purchase.notes.trim());
    }
    return lines.join('\n');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.agreePrivacy) {
      setSnack({ open: true, severity: 'warning', msg: '개인정보처리방침 동의가 필요합니다.' });
      return;
    }
    if (isPurchase && !purchase.quantity) {
      setSnack({ open: true, severity: 'warning', msg: '구매 수량을 입력해주세요.' });
      return;
    }
    if (isOnCooldown()) {
      setSnack({ open: true, severity: 'warning', msg: '잠시 후 다시 시도해주세요.' });
      return;
    }

    const messagePayload = isPurchase ? buildPurchaseMessage() : form.message;

    setLoading(true);
    try {
      const res = await fetch('/api/contact/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          service: serviceKey,
          name: form.name,
          position: form.position,
          email: form.email,
          phone: form.phone,
          company: form.company,
          country: form.country,
          message: messagePayload,
          website: form.website,
        }),
      });

      const data = await res.json();
      if (res.status === 429) {
        setSnack({ open: true, severity: 'error', msg: '요청이 너무 많습니다. 잠시 후 다시 시도해주세요.' });
        return;
      }
      if (!res.ok) {
        setSnack({ open: true, severity: 'error', msg: data.message || '접수 중 오류가 발생했습니다.' });
        return;
      }

      markSubmitted();
      setSubmitted(true);
    } catch {
      setSnack({ open: true, severity: 'error', msg: '네트워크 오류가 발생했습니다. 다시 시도해주세요.' });
    } finally {
      setLoading(false);
    }
  };

  // 접수 완료 화면
  if (submitted) {
    return (
      <Container maxWidth="sm" sx={{ py: '24px' }}>
        <Box sx={{ textAlign: 'center', py: '80px' }}>
          <CheckCircleOutlineIcon sx={{ fontSize: 72, color: teal[400], mb: 3 }} />
          <Typography sx={{ fontFamily: 'NotoSansKR', fontWeight: 900, fontSize: '26px', color: blueGrey[800], mb: 2 }}>
            {isPurchase ? '구입 문의가 접수되었습니다' : '문의가 접수되었습니다'}
          </Typography>
          <Typography sx={{ fontFamily: 'NotoSansKR', fontSize: '15px', color: blueGrey[500], mb: 5, lineHeight: 1.8 }}>
            담당자가 영업일 기준 1~2일 내에<br />입력하신 이메일로 연락드리겠습니다.
          </Typography>
          <Button
            variant="outlined"
            onClick={() => navigate(-1)}
            sx={{ fontFamily: 'NotoSansKR', fontWeight: 700, borderRadius: '50px', px: 4, py: 1.5 }}>
            돌아가기
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <>
      <Container maxWidth="sm" sx={{ py: '24px' }}>
        {/* 헤더 */}
        <Box sx={{ my: 2, padding: '48px 0px 12px' }}>
          <Typography sx={{ fontFamily: 'NotoSansKR', fontWeight: 900, fontSize: '32px', color: blueGrey[800], mb: 0.5 }}>
            문의하기
          </Typography>
          <Typography sx={{ fontFamily: 'NotoSansKR', fontSize: '14px', color: blueGrey[400], mb: 2.5 }}>
            담당자가 영업일 기준 1~2일 내에 연락드립니다.
          </Typography>

          {/* 서비스 선택 */}
          <Typography sx={{ fontFamily: 'NotoSansKR', fontWeight: 700, fontSize: '13px', color: blueGrey[500], mb: 1 }}>
            문의 서비스 선택
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: isProductService ? 2.5 : 3 }}>
            {Object.entries(SERVICE_META).map(([key, meta]) => {
              const active = key === selectedService;
              return (
                <Chip
                  key={key}
                  label={meta.label}
                  onClick={() => handleServiceChange(key)}
                  sx={{
                    fontFamily: 'NotoSansKR',
                    fontWeight: 700,
                    fontSize: '12px',
                    cursor: 'pointer',
                    backgroundColor: active ? meta.color : 'transparent',
                    color: active ? '#fff' : blueGrey[500],
                    border: `1.5px solid ${active ? meta.color : blueGrey[200]}`,
                    transition: 'all 0.18s ease',
                    '&:hover': {
                      backgroundColor: active ? meta.color : `${meta.color}18`,
                      borderColor: meta.color,
                      color: active ? '#fff' : meta.color,
                    },
                  }}
                />
              );
            })}
          </Box>

          {/* 제품 서비스 선택 시 구입/기타 토글 */}
          {isProductService && (
            <Box sx={{ mb: 3 }}>
              <Typography sx={{ fontFamily: 'NotoSansKR', fontWeight: 700, fontSize: '13px', color: blueGrey[500], mb: 1 }}>
                문의 유형
              </Typography>
              <ToggleButtonGroup
                value={inquiryType}
                exclusive
                onChange={(_, val) => { if (val) setInquiryType(val); }}
                size="small"
                sx={{ gap: 1 }}>
                <ToggleButton
                  value="purchase"
                  sx={{
                    fontFamily: 'NotoSansKR',
                    fontWeight: 700,
                    fontSize: '13px',
                    px: 2.5,
                    py: 1,
                    borderRadius: '8px !important',
                    border: `1.5px solid ${blueGrey[200]} !important`,
                    color: blueGrey[500],
                    gap: 0.8,
                    '&.Mui-selected': {
                      backgroundColor: serviceMeta.color,
                      borderColor: `${serviceMeta.color} !important`,
                      color: '#fff',
                      '&:hover': { backgroundColor: serviceMeta.color, filter: 'brightness(1.08)' },
                    },
                    '&:hover': { borderColor: `${serviceMeta.color} !important`, color: serviceMeta.color },
                  }}>
                  <ShoppingCartOutlinedIcon sx={{ fontSize: 16 }} />
                  구입 문의
                </ToggleButton>
                <ToggleButton
                  value="general"
                  sx={{
                    fontFamily: 'NotoSansKR',
                    fontWeight: 700,
                    fontSize: '13px',
                    px: 2.5,
                    py: 1,
                    borderRadius: '8px !important',
                    border: `1.5px solid ${blueGrey[200]} !important`,
                    color: blueGrey[500],
                    gap: 0.8,
                    '&.Mui-selected': {
                      backgroundColor: serviceMeta.color,
                      borderColor: `${serviceMeta.color} !important`,
                      color: '#fff',
                      '&:hover': { backgroundColor: serviceMeta.color, filter: 'brightness(1.08)' },
                    },
                    '&:hover': { borderColor: `${serviceMeta.color} !important`, color: serviceMeta.color },
                  }}>
                  <ChatBubbleOutlineIcon sx={{ fontSize: 16 }} />
                  기타 문의
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
          )}

          <Divider />
        </Box>

        {/* 폼 */}
        <Box component="form" onSubmit={handleSubmit} sx={{ pb: '108px' }}>
          {isPurchase ? (
            /* ── 구입 주문서 폼 ── */
            <Stack direction="column" spacing={2.5}>

              {/* 안내 배너 */}
              <Box sx={{
                backgroundColor: `${serviceMeta.color}12`,
                border: `1px solid ${serviceMeta.color}40`,
                borderRadius: '10px',
                padding: '14px 18px',
              }}>
                <Typography sx={{ fontFamily: 'NotoSansKR', fontWeight: 700, fontSize: '13px', color: serviceMeta.color, mb: 0.5 }}>
                  {SERVICE_META[serviceKey].label} 구입 문의
                </Typography>
                <Typography sx={{ fontFamily: 'NotoSansKR', fontSize: '12px', color: blueGrey[500], lineHeight: 1.7 }}>
                  아래 정보를 입력하시면 담당자가 견적서와 함께 연락드립니다.<br />
                  부가세 별도이며, 수량에 따라 할인이 적용될 수 있습니다.
                </Typography>
              </Box>

              {/* 주문자 정보 */}
              <Typography sx={{ fontFamily: 'NotoSansKR', fontWeight: 800, fontSize: '14px', color: blueGrey[700], mt: 0.5 }}>
                주문자 정보
              </Typography>
              <TextField
                required fullWidth name="name" label="성함"
                value={form.name} onChange={handleChange}
                inputProps={{ maxLength: 100 }} />
              <TextField
                fullWidth name="position" label="직함"
                value={form.position} onChange={handleChange}
                inputProps={{ maxLength: 100 }} />
              <TextField
                required fullWidth name="email" type="email" label="이메일 주소"
                value={form.email} onChange={handleChange}
                inputProps={{ maxLength: 255 }} />
              <FormControl variant="outlined" fullWidth>
                <InputLabel htmlFor="contact-phone">전화번호</InputLabel>
                <OutlinedInput
                  id="contact-phone" name="phone"
                  value={form.phone} onChange={handleChange}
                  label="전화번호" inputComponent={PhoneMask} />
              </FormControl>
              <TextField
                fullWidth name="company" label="기관 / 회사명"
                value={form.company} onChange={handleChange}
                inputProps={{ maxLength: 255 }} />

              <Divider sx={{ my: 0.5 }} />

              {/* 주문 상세 */}
              <Typography sx={{ fontFamily: 'NotoSansKR', fontWeight: 800, fontSize: '14px', color: blueGrey[700] }}>
                주문 상세
              </Typography>

              {/* 구매 수량 */}
              <TextField
                required
                fullWidth
                name="quantity"
                label="구매 수량"
                type="number"
                value={purchase.quantity}
                onChange={handlePurchaseChange}
                inputProps={{ min: 1, max: 9999 }}
                InputProps={{
                  endAdornment: (
                    <Typography sx={{ fontFamily: 'NotoSansKR', fontSize: '14px', color: blueGrey[500], whiteSpace: 'nowrap', pl: 1 }}>
                      대
                    </Typography>
                  ),
                }}
                helperText="10대 이상 구매 시 별도 견적이 제공됩니다."
              />

              <TextField
                fullWidth
                name="deliveryAddress"
                label="납품처 주소"
                placeholder="예) 서울시 강남구 테헤란로 123"
                value={purchase.deliveryAddress}
                onChange={handlePurchaseChange}
                inputProps={{ maxLength: 500 }}
              />

              <TextField
                fullWidth
                name="deliveryDate"
                label="납품 희망 일정"
                placeholder="예) 2026년 6월 초"
                value={purchase.deliveryDate}
                onChange={handlePurchaseChange}
                inputProps={{ maxLength: 100 }}
                helperText="희망 납품 시기를 자유롭게 입력해주세요."
              />

              <TextField
                fullWidth
                name="notes"
                label="추가 요청사항"
                placeholder="특별 요청사항, 설치 환경 등을 자유롭게 남겨주세요."
                multiline
                rows={3}
                value={purchase.notes}
                onChange={handlePurchaseChange}
                inputProps={{ maxLength: 2000 }}
              />

              {/* 허니팟 */}
              <input
                type="text" name="website" value={form.website} onChange={handleChange}
                tabIndex={-1} autoComplete="off" aria-hidden="true"
                style={{ position: 'absolute', opacity: 0, height: 0, width: 0, border: 'none', padding: 0 }}
              />

              <FormControlLabel
                required
                control={<Checkbox name="agreePrivacy" checked={form.agreePrivacy} onChange={handleChange} />}
                label="개인정보처리방침을 확인하였으며, 이에 동의합니다."
              />

              <Button
                type="submit" variant="contained" disabled={loading}
                sx={{
                  fontFamily: 'NotoSansKR', fontWeight: 900, fontSize: '16px',
                  mt: '4px', width: '100%', height: '56px',
                  backgroundColor: serviceMeta.color, borderRadius: '8px',
                  '&:hover': { filter: 'brightness(1.1)' },
                }}>
                {loading ? <CircularProgress size={24} sx={{ color: '#fff' }} /> : '구입 문의 접수하기'}
              </Button>
            </Stack>

          ) : (
            /* ── 일반 문의 폼 ── */
            <Stack direction="column" spacing={2.5}>
              <TextField
                required fullWidth name="name" label="성함"
                value={form.name} onChange={handleChange}
                inputProps={{ maxLength: 100 }} />
              <TextField
                fullWidth name="position" label="직함"
                value={form.position} onChange={handleChange}
                inputProps={{ maxLength: 100 }} />
              <TextField
                required fullWidth name="email" type="email" label="이메일 주소"
                value={form.email} onChange={handleChange}
                inputProps={{ maxLength: 255 }} />
              <FormControl variant="outlined" fullWidth>
                <InputLabel htmlFor="contact-phone">전화번호</InputLabel>
                <OutlinedInput
                  id="contact-phone" name="phone"
                  value={form.phone} onChange={handleChange}
                  label="전화번호" inputComponent={PhoneMask} />
              </FormControl>
              <TextField
                fullWidth name="company" label="기관 / 회사명"
                value={form.company} onChange={handleChange}
                inputProps={{ maxLength: 255 }} />
              <TextField
                fullWidth name="country" label="국가"
                value={form.country} onChange={handleChange}
                inputProps={{ maxLength: 100 }} />
              <TextField
                required fullWidth name="message"
                label="문의 사항을 남겨주세요" multiline rows={4}
                value={form.message} onChange={handleChange}
                inputProps={{ maxLength: 2000 }} />

              {/* 허니팟 */}
              <input
                type="text" name="website" value={form.website} onChange={handleChange}
                tabIndex={-1} autoComplete="off" aria-hidden="true"
                style={{ position: 'absolute', opacity: 0, height: 0, width: 0, border: 'none', padding: 0 }}
              />

              <FormControlLabel
                required
                control={<Checkbox name="agreePrivacy" checked={form.agreePrivacy} onChange={handleChange} />}
                label="개인정보처리방침을 확인하였으며, 이에 동의합니다."
              />

              <Button
                type="submit" variant="contained" disabled={loading}
                sx={{
                  fontFamily: 'NotoSansKR', fontWeight: 900, fontSize: '16px',
                  mt: '4px', width: '100%', height: '56px',
                  backgroundColor: serviceMeta.color, borderRadius: '8px',
                  '&:hover': { filter: 'brightness(1.1)' },
                }}>
                {loading ? <CircularProgress size={24} sx={{ color: '#fff' }} /> : '문의 접수하기'}
              </Button>
            </Stack>
          )}
        </Box>
      </Container>

      <Snackbar
        open={snack.open} autoHideDuration={4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          severity={snack.severity}
          sx={{ fontFamily: 'NotoSansKR', width: '100%' }}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </>
  );
}
